// Copyright 2025 Sorama B.V.
import { CriticalBand } from "./CriticalBand";
import { CriticalBandUnit, UnitType } from "./Unit";

/**
 * Represents a bark band. (similar to critical band, but 24 bands)
 */
export class BarkBand extends UnitType {
    public static override typeName = "Bark Band";

    public static averaged: CriticalBandUnit = new CriticalBandUnit(
        "",
        this.bark24ToHz,
        this.hzToBark24,
        this.subBandToBarkBand,
        BarkBand,
        { unitFamily: CriticalBand }
    );

    // Frequencies corresponding to the bark band bounds, for band 1, this lower bound is index 0 and upper bound is index 1, etc.
    private static readonly boundFrequencies = [
        20, 100, 200, 300, 400, 505, 630, 770, 920, 1075, 1265, 1475, 1710, 1990, 2310, 2690, 3125, 3675, 4350, 5250,
        6350, 7650, 9400, 11750, 15250,
    ];
    /**
     * Converts a critical band number in the Bark scale to its corresponding frequency range in Hz.
     * @param criticalBandValue The critical band number (0.5 to 24.5).
     * @returns A FrequencyRange object containing the center, lower, and upper frequencies in Hz.
     * @throws Error if the critical band number is not between 0.5 and 24.5.
     * @remarks
     * Frequencies above 500 Hz are scaled logarithmically.
     * This bark scale is used for Loudness and Sharpness
     */
    public static bark24ToHz(criticalBandValue: number): number {
        if (criticalBandValue < 0.5 || criticalBandValue > 24.5) {
            throw new Error("Critical band number must be a value between 0.5 and 24.5");
        }

        // Get number and offset of the critical band. e.g. 4.7 => number: 5 and offset: -0.3
        // We have Math.min(x,24) because for 24.5 (which is valid, e.g. when selecting band 24), we need to have a criticalBandNumber (24) which exists in our boundFrequencies array
        const criticalBandNumber = Math.min(Math.round(criticalBandValue), 24);
        const criticalBandOffset = criticalBandValue - (criticalBandNumber - 0.5);

        const lowerBound = BarkBand.boundFrequencies[criticalBandNumber - 1];
        const upperBound = BarkBand.boundFrequencies[criticalBandNumber];

        // Use logarithmic scaling above 500 hz
        if (lowerBound > 500) {
            return Math.pow(
                10,
                Math.log10(lowerBound) + (Math.log10(upperBound) - Math.log10(lowerBound)) * criticalBandOffset
            );
        }

        return lowerBound + (upperBound - lowerBound) * criticalBandOffset;
    }

    /**
     * Converts a frequency in Hz to the corresponding bark band.
     * @param frequency positive frequency
     * @returns The corresponding bark band for values between 0 and 15250 Hz (we also have a 0th bark band to cover 0-20 Hz) Values above 15250 Hz will return 24.5
     * @throws Error if frequency is negative
     * @remarks
     * Frequencies above 500 Hz are scaled logarithmically.
     * This bark scale is used for Loudness and Sharpness
     */
    public static hzToBark24(frequency: number): number {
        if (frequency < 0) {
            throw new Error("Frequency must be a positive number");
        }

        // Just return the largest possible value for frequencies above the highest bark band
        if (frequency > BarkBand.boundFrequencies[BarkBand.boundFrequencies.length - 1]) {
            return 24.5;
        }

        // Set default values so we have a barkBand 0 that goes from 0Hz (-0.5) to 20Hz (0.5)
        let barkBand = 0;
        let lowerBound = -BarkBand.boundFrequencies[0];
        let upperBound = BarkBand.boundFrequencies[0];

        for (let i = 0; i < BarkBand.boundFrequencies.length; i++) {
            if (frequency < BarkBand.boundFrequencies[i]) {
                break;
            }
            barkBand = i + 1;
            lowerBound = BarkBand.boundFrequencies[barkBand - 1];
            upperBound = BarkBand.boundFrequencies[barkBand];
        }

        const offset = (frequency - lowerBound) / (upperBound - lowerBound);
        // Use logarithmic scaling above 500 hz
        if (frequency > 500) {
            return Math.log10(frequency / lowerBound) / Math.log10(upperBound / lowerBound) + barkBand - 0.5;
        }

        return barkBand + offset - 0.5;
    }

    /**
     * Converts a bark band subband index to its corresponding bark band number.
     * Every 10 subbands equals to 1 full bark band
     * The subbands are defined from index 1
     * And each band should be spaced around its bark band value equally, so
     * [1,2,3,4,5,6,7,8,9,10] => [0.55,0.65,0.75,0.85,0.95,1.05,1.15,1.25,1.35,1.45]
     * @param subBand The subband index (1 to 10)
     * @returns The corresponding bark band value for the subband index
     */
    public static subBandToBarkBand(subBand: number): number {
        const subBandsPerBand = 10;
        // The zero based index of the subband that should be in the middle, or exactly on the band value
        // if the number of subbands is odd it will be an index (in this case) of 4.5, it's the middle of [0,9]
        const indexOfMiddleSubBand = (subBandsPerBand - 1) / 2;

        return (subBand + indexOfMiddleSubBand) / subBandsPerBand;
    }
}
